import React, { createContext, useContext } from 'react';
import * as Location from './fields/Location';
import * as Dates from './fields/Dates';
import * as Occupancy from './fields/Occupancy';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { SubmitButton } from './components/SubmitButton';
import { FormFieldsWrapper } from './components/FormFieldsWrapper';
import { StaysSearchTerms } from '@apps/stays/types';
import { Box, Divider, Flex } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { convertToDateRangeFromDateParts } from '@shared/utils/dateHelpers';
import { useTranslation } from '@shared/core';

const DEFAULT_CONFIG: SearchFormConfig = {
  dateRangeEnabled: true,
  occupancySelectorEnabled: true,
  isOpenedFormView: false,
  isCompactVersion: false,
} as const;

interface SearchFormConfig
  extends Readonly<{
    dateRangeEnabled: boolean;
    occupancySelectorEnabled: boolean;
    isOpenedFormView: boolean;
    isCompactVersion?: boolean;
  }> {}

const SearchFormConfigContext = createContext<SearchFormConfig>(DEFAULT_CONFIG);

export const useSearchFormConfig = () => {
  return useContext(SearchFormConfigContext);
};

interface Props {
  onSubmit: (value: StaysSearchTerms) => void;
  initialValue?: Partial<StaysSearchTerms>;
  config?: Readonly<{
    dateRangeEnabled?: boolean;
    occupancySelectorEnabled?: boolean;
    isOpenedFormView?: boolean;
    isCompactVersion?: boolean;
  }>;
}

export const LocationDatesOccupants = (props: Props) => {
  const today = new Date();
  const threeDaysFromNow = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000);

  const searchForm: SearchFormConfig = {
    dateRangeEnabled: props.config?.dateRangeEnabled ?? DEFAULT_CONFIG.dateRangeEnabled,
    occupancySelectorEnabled: props.config?.occupancySelectorEnabled ?? DEFAULT_CONFIG.occupancySelectorEnabled,
    isOpenedFormView: props.config?.isOpenedFormView ?? DEFAULT_CONFIG.isOpenedFormView,
    isCompactVersion: props.config?.isCompactVersion ?? DEFAULT_CONFIG.isCompactVersion,
  };

  const formHeight = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled ? 'fit-content' : '100%';
  const showDatesAndOccupancies = searchForm.dateRangeEnabled || searchForm.occupancySelectorEnabled;

  const { t } = useTranslation('stays');
  const datesOccupantsTrans = t('datesOccupants');
  const validation = {
    childAgeInvalid: datesOccupantsTrans.provideChildAge(),
    childAgeRequired: datesOccupantsTrans.provideChildAge(),
    childAgeMin: datesOccupantsTrans.validation.childAgeMin(),
    childAgeMax: datesOccupantsTrans.validation.childAgeMax(),
  };

  const schema = Yup.object({
    location: Location.schema,
    dates: Dates.schema,
    occupancy: Occupancy.createSchema(validation),
  });
  return (
    <SearchFormConfigContext.Provider value={searchForm}>
      <Formik
        initialValues={{
          location: {
            displayName: '',
            latitude: 0,
            longitude: 0,
            googlePlaceId: '',
          },
          // what I would love to do is use 'satisfies' the type of Dates.initialValue
          dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
          // TODO: fix childrenAges type
          occupancy: props.initialValue?.rooms || Occupancy.initialValue,
        }}
        onSubmit={value => {
          props.onSubmit({
            destination: value.location.displayName,
            lat: value.location.latitude,
            lng: value.location.longitude,
            googlePlaceId: value.location.googlePlaceId,
            startDate: {
              year: value.dates.startDate.getFullYear(),
              month: value.dates.startDate.getMonth() + 1,
              day: value.dates.startDate.getDate(),
            },
            endDate: {
              year: value.dates.endDate.getFullYear(),
              month: value.dates.endDate.getMonth() + 1,
              day: value.dates.endDate.getDate(),
            },
            rooms: value.occupancy.map(room => ({
              adults: room.adults,
              childrenAges: room.childrenAges,
            })),
          });
        }}
        validationSchema={schema}
      >
        <Form
          name="search-fields"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: formHeight }}
        >
          {/* TODO: think about how to make this easier to do across different forms, maybe custom provider */}
          {/* Trick to reset the form after async initial values come is */}
          <ResetFormOnNewInitialValue initialValue={props.initialValue} />
          {searchForm.isOpenedFormView ? (
            <>
              <Flex
                paddingX={6}
                paddingBottom={6}
                width={'100%'}
                flexDirection={'column'}
                height={formHeight}
                justifyContent={!showDatesAndOccupancies ? 'space-between' : 'flex-start'}
              >
                <Location.Fields fieldsId={'location'} />
                {showDatesAndOccupancies ? (
                  <>
                    {searchForm.dateRangeEnabled && (
                      <Dates.Fields
                        fieldsId={'dates'}
                        defaultStartDate={today}
                        defaultEndDate={threeDaysFromNow}
                      />
                    )}
                    {searchForm.occupancySelectorEnabled && <Occupancy.Fields fieldsId={'occupancy'} />}
                  </>
                ) : null}
              </Flex>
              <SubmitButton />
            </>
          ) : (
            <>
              {/* Mobile View - Vertically Stacked (or forced mobile version) */}
              <Flex
                display={searchForm.isCompactVersion ? 'flex' : { _: 'flex', md: 'none' }}
                width={'100%'}
                flexDirection={'column'}
                backgroundColor="white"
                borderRadius={4}
                gap={4}
                padding={4}
              >
                <Location.Fields fieldsId={'location'} />
                {showDatesAndOccupancies && (
                  <>
                    {searchForm.dateRangeEnabled && (
                      <Dates.Fields
                        fieldsId={'dates'}
                        defaultStartDate={today}
                        defaultEndDate={threeDaysFromNow}
                      />
                    )}
                    {searchForm.occupancySelectorEnabled && <Occupancy.Fields fieldsId={'occupancy'} />}
                  </>
                )}
                <SubmitButton />
              </Flex>

              {/* Desktop View - Horizontally Aligned (hidden when isCompactVersion is true) */}
              {!searchForm.isCompactVersion && (
                <Flex
                  display={{ _: 'none', md: 'flex' }}
                  alignItems="center"
                  gap={3}
                >
                  <FormFieldsWrapper>
                    <Box
                      gridArea={'location'}
                      height={pxToRem(24)}
                      minWidth={pxToRem(260)}
                    >
                      <Location.Fields fieldsId={'location'} />
                    </Box>
                    {showDatesAndOccupancies ? (
                      <>
                        {searchForm.dateRangeEnabled && (
                          <>
                            <Divider
                              orientation="vertical"
                              height={pxToRem(24)}
                            />
                            <Box
                              gridArea={'dates'}
                              height={pxToRem(24)}
                            >
                              <Dates.Fields
                                fieldsId={'dates'}
                                defaultStartDate={today}
                                defaultEndDate={threeDaysFromNow}
                              />
                            </Box>
                            <Divider
                              orientation="vertical"
                              height={pxToRem(24)}
                            />
                          </>
                        )}
                        {searchForm.occupancySelectorEnabled && (
                          <Box gridArea={'occupancy'}>
                            <Occupancy.Fields fieldsId={'occupancy'} />
                          </Box>
                        )}
                      </>
                    ) : null}
                    <Box gridArea={'submit'}>
                      <SubmitButton />
                    </Box>
                  </FormFieldsWrapper>
                </Flex>
              )}
            </>
          )}
        </Form>
      </Formik>
    </SearchFormConfigContext.Provider>
  );
};

const ResetFormOnNewInitialValue = (props: Pick<Props, 'initialValue'>) => {
  const formik = useFormikContext();
  React.useEffect(() => {
    formik.resetForm({
      values: {
        location: {
          displayName: props.initialValue?.destination,
          latitude: props.initialValue?.lat,
          longitude: props.initialValue?.lng,
          googlePlaceId: props.initialValue?.googlePlaceId || '',
        },
        dates: convertToDateRangeFromDateParts(props.initialValue?.startDate, props.initialValue?.endDate),
        occupancy: props.initialValue?.rooms || Occupancy.initialValue,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialValue]);
  return null;
};
